import type { TFunction } from 'i18next';

import type { PricingReadiness, PricingReviewSummary } from '@/lib/api';

export type RepricingTab = 'review' | 'queue' | 'history';

/** Mode rule: a large round reviews on "Controleren", a small one lists its
 *  proposals on "Voorstellen". Never both: one way in per mode. */
export function tabsFor(showOverview: boolean): readonly [RepricingTab, RepricingTab] {
  return showOverview ? ['review', 'history'] : ['queue', 'history'];
}

/** The sidebar badge: what is still to handle. Small mode: every open
 *  proposal (te beoordelen); big mode: only the check bucket (nakijken). */
export function openBadge(summary: Pick<PricingReviewSummary, 'show_overview' | 'buckets'> | undefined): number {
  if (!summary) return 0;
  const b = summary.buckets;
  return summary.show_overview ? b.check : b.safe + b.check;
}

/** "N met een voorstel" in the header. Big mode: the review summary's
 *  total, the number the overview card repeats. Small mode: pending +
 *  approved, next to the blocked count, as the design reads. */
export function proposalCount(
  summary: Pick<PricingReviewSummary, 'show_overview' | 'open'> | undefined,
  readiness: Partial<Pick<PricingReadiness, 'pending' | 'approved'>> | undefined,
): number | undefined {
  if (!summary) return undefined;
  if (summary.show_overview) return summary.open;
  const { pending, approved } = readiness ?? {};
  return pending !== undefined && approved !== undefined ? pending + approved : undefined;
}

// The guardrail's block reason for "no minimum price" (pricing/guardrails.py).
const NO_FLOOR = 'no_min_price';

/** The blocked card's sub line: "33 zonder minimumprijs · 2 met verdachte
 *  data". Every other reason counts as suspect data. Null without a split. */
export function blockedNote(
  t: TFunction,
  reasons: PricingReviewSummary['blocked_by_reason'] | undefined,
  fmt: (n: number) => string,
): string | null {
  if (!reasons?.length) return null;
  const noFloor = reasons.filter((r) => r.reason === NO_FLOOR).reduce((s, r) => s + r.n, 0);
  const suspect = reasons.filter((r) => r.reason !== NO_FLOOR).reduce((s, r) => s + r.n, 0);
  const parts = [
    noFloor ? t('repricing.strip.blockedNoFloor', { n: fmt(noFloor) }) : null,
    suspect ? t('repricing.strip.blockedSuspect', { n: fmt(suspect) }) : null,
  ].filter((p): p is string => p !== null);
  return parts.length ? parts.join(' · ') : null;
}

// The customer's own shop platform, as he knows it from his own admin.
const PLATFORM: Record<string, string> = { shopify: 'Shopify', woocommerce: 'WooCommerce', magento: 'Magento' };

/** "WooCommerce NL": the shop a round writes to, as the customer knows it. */
export function shopLabel(platform: string, country: string): string {
  return `${PLATFORM[platform] ?? platform} ${country}`;
}

type Counts = Partial<Pick<PricingReadiness, 'monitored_skus' | 'blocked' | 'generated_at'>>;

/** The header's grey line: "5.214 producten · 4.600 met een voorstel · 50
 *  geblokkeerd · WooCommerce NL". `proposals` comes from `proposalCount`. A part whose number we do
 *  not have is left out; the time only in small mode (big mode shows it in
 *  its overview). */
export function summaryLine(
  t: TFunction,
  locale: string,
  {
    readiness: r,
    proposals,
    shop,
    showTime,
    now = new Date(),
  }: {
    readiness: Counts | undefined;
    proposals: number | undefined;
    shop: { platform: string; country: string } | null;
    showTime: boolean;
    now?: Date;
  },
): string {
  if (!r) return '';
  const parts: string[] = [];
  const add = (key: string, v: number | undefined) => {
    if (v !== undefined) parts.push(t(key, { count: v, n: v.toLocaleString(locale) }));
  };
  add('repricing.line.products', r.monitored_skus);
  add('repricing.line.withProposal', proposals);
  add('repricing.line.blocked', r.blocked);
  if (showTime && r.generated_at) {
    const at = new Date(r.generated_at);
    const time = at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    const when = at.toDateString() === now.toDateString()
      ? t('repricing.line.today', { time })
      : `${at.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} ${time}`;
    parts.push(t('repricing.line.generated', { when }));
  }
  if (shop) parts.push(shopLabel(shop.platform, shop.country));
  return parts.join(' · ');
}
